// ============================================================================
// Chekdagi rasmlar (Q-068): oq-qora (1 bit) rasm — termoprinter faqat qora nuqta bosadi.
// Sof funksiyalar (brauzersiz) — bitmap.test.ts da testlanadi. Rasmni tayyorlash (canvas) — bitmapCanvas.ts.
//
// Saqlash formati (restoran sozlamasi, matn): "mb1:<eni>x<bo'yi>:<base64>"
//   qatorlar ketma-ket, har qatorda ceil(eni/8) bayt, katta bit — chapdagi nuqta, 1 — qora.
// ============================================================================

export interface MonoBitmap {
  w: number;
  h: number;
  /** Qatorma-qator, har qatorda bytesPerRow(w) bayt */
  bits: Uint8Array;
}

/** Saqlanadigan eng katta o'lcham (58 mm qog'oz eni — 384 nuqta) */
export const MAX_W = 384;
export const MAX_H = 400;

export const bytesPerRow = (w: number) => Math.ceil(w / 8);

export function blank(w: number, h: number): MonoBitmap {
  return { w, h, bits: new Uint8Array(bytesPerRow(w) * h) };
}

export function getPx(b: MonoBitmap, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= b.w || y >= b.h) return false;
  return ((b.bits[y * bytesPerRow(b.w) + (x >> 3)]! >> (7 - (x & 7))) & 1) === 1;
}

export function setPx(b: MonoBitmap, x: number, y: number, on: boolean): void {
  if (x < 0 || y < 0 || x >= b.w || y >= b.h) return;
  const i = y * bytesPerRow(b.w) + (x >> 3);
  const m = 1 << (7 - (x & 7));
  b.bits[i] = on ? b.bits[i]! | m : b.bits[i]! & ~m;
}

// ---------------------------------------------------------------------------
// Matn ko'rinishi (base64 — brauzer va node'da bir xil, kutubxonasiz)
// ---------------------------------------------------------------------------
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function toB64(a: Uint8Array): string {
  let s = '';
  for (let i = 0; i < a.length; i += 3) {
    const n = (a[i]! << 16) | ((a[i + 1] ?? 0) << 8) | (a[i + 2] ?? 0);
    s += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]! + (i + 1 < a.length ? B64[(n >> 6) & 63]! : '=') + (i + 2 < a.length ? B64[n & 63]! : '=');
  }
  return s;
}

function fromB64(s: string): Uint8Array | null {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s) || s.length % 4 !== 0) return null;
  const pad = s.endsWith('==') ? 2 : s.endsWith('=') ? 1 : 0;
  const out = new Uint8Array((s.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < s.length; i += 4) {
    const n = (B64.indexOf(s[i]!) << 18) | (B64.indexOf(s[i + 1]!) << 12) | ((B64.indexOf(s[i + 2]!) & 63) << 6) | (B64.indexOf(s[i + 3]!) & 63);
    if (o < out.length) out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}

export function encodeMono(b: MonoBitmap): string {
  return `mb1:${b.w}x${b.h}:${toB64(b.bits)}`;
}

/** Noto'g'ri, juda katta yoki buzilgan qiymat — null (chekda rasm chiqmaydi, xato bermaydi) */
export function decodeMono(s: unknown): MonoBitmap | null {
  if (typeof s !== 'string') return null;
  const m = /^mb1:(\d{1,4})x(\d{1,4}):([A-Za-z0-9+/=]*)$/.exec(s);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (w < 1 || h < 1 || w > MAX_W || h > MAX_H) return null;
  const bits = fromB64(m[3]!);
  if (!bits || bits.length !== bytesPerRow(w) * h) return null;
  return { w, h, bits };
}

// ---------------------------------------------------------------------------
// O'zgartirish
// ---------------------------------------------------------------------------
/** Kichraytirish/kattalashtirish: maydon bo'yicha o'rtacha qoralik (yarmidan ko'pi qora — qora) */
export function scaleMono(b: MonoBitmap, w: number, h: number): MonoBitmap {
  w = Math.max(1, Math.round(w));
  h = Math.max(1, Math.round(h));
  if (w === b.w && h === b.h) return b;
  const out = blank(w, h);
  const sx = b.w / w;
  const sy = b.h / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * sy);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * sx);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let on = 0;
      let all = 0;
      for (let yy = y0; yy < y1; yy++)
        for (let xx = x0; xx < x1; xx++) {
          all++;
          if (getPx(b, xx, yy)) on++;
        }
      if (on * 2 >= all && on > 0) setPx(out, x, y, true);
    }
  }
  return out;
}

/** Qog'oz eniga markazlash (printer "markazlash" buyrug'ini rasmga qo'llamasligi mumkin) */
export function padCenter(b: MonoBitmap, width: number): MonoBitmap {
  if (b.w >= width) return b.w === width ? b : scaleMono(b, width, Math.round((b.h * width) / b.w));
  const out = blank(width, b.h);
  const dx = Math.floor((width - b.w) / 2);
  for (let y = 0; y < b.h; y++) for (let x = 0; x < b.w; x++) if (getPx(b, x, y)) setPx(out, x + dx, y, true);
  return out;
}

/** Bo'sh (oq) chetlarni olib tashlash — rasm qog'ozda ortiqcha joy egallamasin */
export function trimMono(b: MonoBitmap): MonoBitmap | null {
  let x0 = b.w;
  let y0 = b.h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < b.h; y++)
    for (let x = 0; x < b.w; x++)
      if (getPx(b, x, y)) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
  if (x1 < 0) return null;
  const out = blank(x1 - x0 + 1, y1 - y0 + 1);
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (getPx(b, x, y)) setPx(out, x - x0, y - y0, true);
  return out;
}

/** Kulrang piksellardan (0 — qora, 255 — oq) oq-qora rasm: qoralik 0..100 (50 — o'rtacha) */
export function monoFromGray(gray: ArrayLike<number>, w: number, h: number, darkness = 50): MonoBitmap {
  const out = blank(w, h);
  // Qoralik oshsa — chegara ko'tariladi (ko'proq nuqta qora bo'ladi)
  const limit = 64 + (Math.max(0, Math.min(100, darkness)) / 100) * 160;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (gray[y * w + x]! < limit) setPx(out, x, y, true);
  return out;
}

// ---------------------------------------------------------------------------
// ESC/POS: GS v 0 — rastr rasm (64 qatorlik bo'laklarda: printer xotirasi kichik bo'lishi mumkin)
// ---------------------------------------------------------------------------
export function rasterBytes(b: MonoBitmap): number[] {
  const row = bytesPerRow(b.w);
  const out: number[] = [];
  for (let y = 0; y < b.h; y += 64) {
    const n = Math.min(64, b.h - y);
    out.push(0x1d, 0x76, 0x30, 0, row & 255, row >> 8, n & 255, n >> 8);
    for (let i = y * row; i < (y + n) * row; i++) out.push(b.bits[i]!);
  }
  return out;
}

/** Logotip kattaligi: qog'oz enining ulushi (58 mm bo'yicha) */
export const LOGO_WIDTH: Record<'S' | 'M' | 'L', number> = { S: 160, M: 256, L: 384 };
